import { API_URL } from '@/common/config'
import jwtDefaultConfig from './jwtDefaultConfig'

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // For Refreshing Token
  subscribers = []

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // Request Interceptor
    this.axiosIns.interceptors.request.use(
      config => {
        // Lazy require to avoid circular dependency: store -> jwtService -> store
        // eslint-disable-next-line global-require
        const store = require('@/store').default
        const accessToken = store.getters['auth/getAccessToken']

        if (accessToken) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }

        config.baseURL = API_URL

        return config
      },
      error => Promise.reject(error),
    )

    // Response Interceptor: handle 401 with silent token refresh + queued retry
    this.axiosIns.interceptors.response.use(
      response => response,
      error => {
        const status = error.response ? error.response.status : null
        const originalRequest = error.config

        // If the refresh endpoint itself fails, immediately logout.
        // Do NOT retry — avoids infinite loop and hanging subscribers.
        if (originalRequest && originalRequest.url === this.jwtConfig.refreshEndpoint && status === 401) {
          this.isAlreadyFetchingAccessToken = false
          this.subscribers = []
          // eslint-disable-next-line global-require
          const store = require('@/store').default
          store.dispatch('auth/logout').then(() => {
            // eslint-disable-next-line global-require
            const router = require('@/router').default
            if (router.currentRoute.name !== 'login') {
              router.push({ name: 'login' })
            }
          })
          return Promise.reject(error)
        }

        if (status === 401) {
          if (!this.isAlreadyFetchingAccessToken) {
            this.isAlreadyFetchingAccessToken = true
            // eslint-disable-next-line global-require
            const store = require('@/store').default

            store.dispatch('auth/refreshToken')
              .then(newToken => {
                this.isAlreadyFetchingAccessToken = false
                this.onAccessTokenFetched(newToken)
              })
              .catch((refreshError) => {
                this.isAlreadyFetchingAccessToken = false
                this.subscribers = []

                // Extract the status code from the refresh error
                const refreshStatus = refreshError.response ? refreshError.response.status : null
              
                // ONLY logout if the refresh token explicitly returned 401
                if (refreshStatus === 401) {
                  store.dispatch('auth/logout').then(() => {
                    // eslint-disable-next-line global-require
                    const router = require('@/router').default
                    if (router.currentRoute.name !== 'login') {
                      router.push({ name: 'login' })
                    }
                  })
                } else {
                  this.subscribers = []
                }
              })
          }

          // Queue the original request to retry after token refresh
          return new Promise(resolve => {
            this.addSubscriber(newToken => {
              // eslint-disable-next-line no-param-reassign
              originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${newToken}`
              resolve(this.axiosIns(originalRequest))
            })
          })
        }

        return Promise.reject(error)
      },
    )
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers.forEach(callback => callback(accessToken))
    this.subscribers = []
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  setUserData(userData) {
    localStorage.setItem(this.jwtConfig.storageUserDataName, JSON.stringify(userData))
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }
}
